import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@nrwl/environment';
import { Form } from '@nrwl/rx-models';
import { map, Observable } from 'rxjs';
import { rethrowAmendmentLockConflict } from './amendment-lock.service';

/**
 * Payload for requesting an amendment draft from a completed form.
 */
export interface CreateAmendmentDraftPayload {
  /**
   * Source completed form ID used by the amendment draft endpoint.
   */
  readonly formId: number;
}

/**
 * Params for loading amendment version history for a form document.
 */
export interface GetVersionHistoryParams {
  /**
   * Form ID whose document versions should be returned.
   */
  readonly formId: number;
}

/**
 * Frontend lifecycle status for a dynamic form version.
 */
export type DynamicFormVersionStatus =
  | 'audit-needed'
  | 'incomplete'
  | 'director-review'
  | 'supervisor-review'
  | 'complete'
  | 'ongoing-plan';

/**
 * Action represented by the actor metadata of a dynamic form version.
 */
export type DynamicFormVersionActorAction = 'created' | 'amended';

/**
 * Metadata for one physical form version in a logical form's version history.
 */
export interface DynamicFormVersion {
  /**
   * Unique identifier of this physical dynamic-form version.
   */
  readonly id: number;

  /**
   * ID of the original V1 record, or null when this record is the original or is not versioned.
   */
  readonly originalVersion: number | null;

  /**
   * ID of the current completed version of record, or null when no completed version is recorded.
   */
  readonly currentVersion: number | null;

  /**
   * ID of the immediately preceding version, or null for the original version.
   */
  readonly previousVersion: number | null;

  /**
   * One-based position in the version sequence, or null before a legacy form is versioned.
   */
  readonly versionNumber: number | null;

  /**
   * Current lifecycle status, including Ongoing Plan records.
   */
  readonly status: DynamicFormVersionStatus;

  /**
   * Date and time this physical version was created.
   */
  readonly createdAt: Date;

  /**
   * ID of the doctor recorded as the author of this version.
   */
  readonly createdBy: number;

  /**
   * Display name of the doctor recorded as the author of this version.
   */
  readonly createdByName: string;

  /**
   * Date and time the amendment was initiated, or null for an original version.
   */
  readonly amendedAt: Date | null;

  /**
   * ID of the user who initiated the amendment, or null for an original version.
   */
  readonly amendedBy: number | null;

  /**
   * Display name of the user who initiated the amendment, or null for an original version.
   */
  readonly amendedByName: string | null;

  /**
   * Date and time this version was completed, or null while it remains unfinished.
   */
  readonly completedAt: Date | null;

  /**
   * ID of the user who completed this version, or null when that user was not recorded.
   */
  readonly completedBy: number | null;

  /**
   * Display name of the user who completed this version, or null when that user was not recorded.
   */
  readonly completedByName: string | null;

  /**
   * Publication or catalogued date associated with this version.
   */
  readonly publicationDate: Date;

  /**
   * Whether this version is currently shared with the patient.
   */
  readonly sharedWithPatient: boolean;

  /**
   * Whether this version is archived through the dynamic form's soft-delete state.
   */
  readonly isDeleted: boolean;

  /**
   * Number of history-visible versions, including the original and completed amendments.
   */
  readonly totalVersions: number;

  /**
   * Whether this version is the current completed version of record.
   */
  readonly isCurrentVersion: boolean;

  /**
   * Whether this is the newest non-archived stored version, including an active amendment draft.
   */
  readonly isLatestVersion: boolean;

  /**
   * Whether this version currently has a cached PDF artifact.
   */
  readonly hasPdf: boolean;

  /**
   * Whether the current user may initiate an amendment from this version.
   */
  readonly canAmend: boolean;

  /**
   * Number of completed amendments beyond the original version.
   */
  readonly amendmentCount: number;

  /**
   * IDs of form-field widgets that should display amendment indicators for this version.
   */
  readonly changedFieldIds: readonly string[];

  /**
   * Display name of the user represented in this version's creation or amendment label.
   */
  readonly versionActorName: string;

  /**
   * Action performed by the version actor.
   */
  readonly versionActorAction: DynamicFormVersionActorAction;

  /**
   * Date used alongside the actor in version-history labels.
   */
  readonly versionDisplayDate: Date;
}

/**
 * Returns completed form versions in the oldest-first order expected by version navigation.
 */
export function getCompletedFormVersionsOldestFirst(
  versions: readonly DynamicFormVersion[],
): readonly DynamicFormVersion[] {
  return [...versions]
    .filter(({ status }) => status === 'complete')
    .sort(
      (left, right) => (left.versionNumber ?? 0) - (right.versionNumber ?? 0) || left.id - right.id,
    );
}

/**
 * Returns completed form versions followed by the form currently opened in transcript view.
 * An unfinished current amendment is excluded from completed history but must remain selectable.
 */
export function getTranscriptFormVersionsOldestFirst<TCurrent extends { readonly id: number }>(
  versions: readonly DynamicFormVersion[],
  currentVersion: TCurrent,
): readonly (DynamicFormVersion | TCurrent)[] {
  const completedVersions = getCompletedFormVersionsOldestFirst(versions);

  return completedVersions.some(({ id }) => id === currentVersion.id)
    ? completedVersions
    : [...completedVersions, currentVersion];
}

/**
 * Picks the version an amendment must be created from: the current completed version of record.
 * The version the UI opened with can be stale when another user completed a newer amendment in
 * the meantime. Falls back to the given ID when history is unavailable or has no completed version.
 */
export function resolveAmendmentSourceFormId(
  versions: readonly DynamicFormVersion[],
  fallbackFormId: number,
): number {
  return versions.find((version) => version.isCurrentVersion)?.id ?? fallbackFormId;
}

@Injectable({ providedIn: 'root' })
export class FormAmendmentsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseAPIUrlv3}form`;

  /**
   * Requests an editable amendment draft for the completed source form.
   * Rejects with {@link AmendmentLockConflictError} when another user holds the amendment lock (409).
   */
  public createAmendmentDraft(payload: CreateAmendmentDraftPayload): Observable<Form> {
    return this.http
      .post<Form>(`${this.baseUrl}/${payload.formId}/amend/`, null)
      .pipe(rethrowAmendmentLockConflict());
  }

  /**
   * Loads version history for the form document.
   */
  public getVersionHistory(
    params: GetVersionHistoryParams,
  ): Observable<readonly DynamicFormVersion[]> {
    return this.http
      .get<unknown>(`${this.baseUrl}/${params.formId}/versions/`)
      .pipe(map((response) => this.parseVersionHistoryResponse(response)));
  }

  /**
   * Parses a version-history response into normalized dynamic-form versions.
   */
  private parseVersionHistoryResponse(response: unknown): readonly DynamicFormVersion[] {
    if (!Array.isArray(response)) {
      throw new Error('Invalid dynamic form version history response.');
    }

    return response.map((item) => this.parseDynamicFormVersion(item));
  }

  /**
   * Parses one dynamic-form version record from the API response.
   */
  private parseDynamicFormVersion(value: unknown): DynamicFormVersion {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error('Invalid dynamic form version record.');
    }

    const record = value as Record<string, unknown>;

    return {
      id: this.parseNumber(record['id']),
      originalVersion: this.parseNullableNumber(record['original_version']),
      currentVersion: this.parseNullableNumber(record['current_version']),
      previousVersion: this.parseNullableNumber(record['previous_version']),
      versionNumber: this.parseNullableNumber(record['version_number']),
      status: this.parseStatus(record['status']),
      createdAt: this.parseDate(record['created_at']),
      createdBy: this.parseNumber(record['created_by']),
      createdByName: this.parseString(record['created_by_name']),
      amendedAt: this.parseNullableDate(record['amended_at']),
      amendedBy: this.parseNullableNumber(record['amended_by']),
      amendedByName: this.parseNullableString(record['amended_by_name']),
      completedAt: this.parseNullableDate(record['completed_at']),
      completedBy: this.parseNullableNumber(record['completed_by']),
      completedByName: this.parseNullableString(record['completed_by_name']),
      publicationDate: this.parseDate(record['publication_date']),
      sharedWithPatient: this.parseBoolean(record['shared_with_patient']),
      isDeleted: this.parseBoolean(record['is_deleted']),
      totalVersions: this.parseNumber(record['total_versions']),
      isCurrentVersion: this.parseBoolean(record['is_current_version']),
      isLatestVersion: this.parseBoolean(record['is_latest_version']),
      hasPdf: this.parseBoolean(record['has_pdf']),
      canAmend: this.parseBoolean(record['can_amend']),
      amendmentCount: this.parseNumber(record['amendment_count']),
      changedFieldIds: this.parseStringArray(record['changed_field_ids']),
      versionActorName: this.parseString(record['version_actor_name']),
      versionActorAction: this.parseVersionActorAction(record['version_actor_action']),
      versionDisplayDate: this.parseDate(record['version_display_date']),
    };
  }

  private parseStatus(value: unknown): DynamicFormVersionStatus {
    switch (value) {
      case 1:
        return 'audit-needed';
      case 2:
        return 'incomplete';
      case 3:
        return 'director-review';
      case 4:
        return 'supervisor-review';
      case 5:
        return 'complete';
      case 6:
        return 'ongoing-plan';
      default:
        throw new Error('Invalid dynamic form version status.');
    }
  }

  private parseVersionActorAction(value: unknown): DynamicFormVersionActorAction {
    if (value !== 'created' && value !== 'amended') {
      throw new Error('Invalid dynamic form version actor action.');
    }

    return value;
  }

  private parseNumber(value: unknown): number {
    if (typeof value !== 'number') {
      throw new Error('Invalid number.');
    }

    return value;
  }

  private parseNullableNumber(value: unknown): number | null {
    if (value === null) {
      return null;
    }

    return this.parseNumber(value);
  }

  private parseString(value: unknown): string {
    if (typeof value !== 'string') {
      throw new Error('Invalid string.');
    }

    return value;
  }

  private parseNullableString(value: unknown): string | null {
    if (value === null) {
      return null;
    }

    return this.parseString(value);
  }

  private parseBoolean(value: unknown): boolean {
    if (typeof value !== 'boolean') {
      throw new Error('Invalid boolean.');
    }

    return value;
  }

  private parseDate(value: unknown): Date {
    const rawValue = this.parseString(value);
    const date = new Date(rawValue);

    if (Number.isNaN(date.getTime())) {
      throw new Error('Invalid date.');
    }

    return date;
  }

  private parseNullableDate(value: unknown): Date | null {
    if (value === null) {
      return null;
    }

    return this.parseDate(value);
  }

  private parseStringArray(value: unknown): readonly string[] {
    if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) {
      throw new Error('Invalid string array.');
    }

    return value;
  }
}
